import { createContext, useContext, useId, type ReactNode } from 'react';
import { cx } from '../utils/cx.js';

/**
 * ラベル / 補足 / エラーを入力欄に紐づける（05 §3.8-5: 読み上げで「金額：入力してください」と聞こえること）。
 * 中の Input / Select / Textarea は FieldContext から id・aria-describedby・aria-invalid を受け取る。
 */
export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FormFieldProps {
  label: ReactNode;
  children: ReactNode;
  id?: string;
  required?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  /** チェックボックスのように label 要素を使わない場合 */
  as?: 'label' | 'div';
  className?: string;
  /** ラベルの右に置く補助（画面内ヘルプなど） */
  labelExtra?: ReactNode;
}

export function FormField({ label, children, id: givenId, required = false, help, error, as = 'label', className, labelExtra }: FormFieldProps) {
  const autoId = useId();
  const id = givenId ?? `f${autoId}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;
  const LabelTag = as === 'label' ? 'label' : 'div';

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cx('flex flex-col gap-1.5', className)}>
        <div className="flex items-center gap-2">
          <LabelTag htmlFor={as === 'label' ? id : undefined} className="text-callout font-medium text-fg">
            {label}
            {required ? (
              <span className="ml-1 text-danger" aria-hidden="true">
                *
              </span>
            ) : null}
            {required ? <span className="sr-only">（必須）</span> : null}
          </LabelTag>
          {labelExtra}
        </div>
        {children}
        {error ? (
          <p id={errorId} className="flex items-start gap-1 text-caption text-danger" role="alert">
            {error}
          </p>
        ) : null}
        {help ? (
          <p id={helpId} className="text-caption text-fg-secondary">
            {help}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/** 複数の欄を並べるときのグリッド（640px 未満では縦積み） */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid grid-cols-1 gap-5 sm:grid-cols-2', className)}>{children}</div>;
}

/** フォーム全体に紐づかないエラー（"_"） */
export function FormError({ message }: { message?: ReactNode }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-input border border-danger px-4 py-3 text-callout text-danger">
      {message}
    </div>
  );
}
